// src/app/settings/accruals/pages/leave-policy-detail/leave-policy-detail.component.ts

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AccrualsService } from '../../accruals.service';
import {
  EmployeeUser,
  LeavePolicyDetail,
  UserFilterValues,
} from '../../models/accruals.model';

interface PolicyAssignmentFiltersVm {
  searchTerm: string;
  companyId: number | null;
  departmentId: number | null;
}

@Component({
  selector: 'app-leave-policy-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './leave-policy-detail.component.html',
  styleUrls: ['./leave-policy-detail.component.scss'],
})
export class LeavePolicyDetailComponent implements OnInit, OnDestroy {
  isLoading = false;
  isAssigningPolicy = false;
  errorMessage: string | null = null;

  policyId: number | null = null;
  policy: LeavePolicyDetail | null = null;

  activeUsers: EmployeeUser[] = [];
  userFilterValues: UserFilterValues | null = null;

  assignModalOpen = false;
  assignmentStartDate = '';
  selectedEmployeeIds = new Set<number>();

  assignmentFilters: PolicyAssignmentFiltersVm = {
    searchTerm: '',
    companyId: null,
    departmentId: null,
  };

  assignmentSearchInputValue = '';

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly accrualsService: AccrualsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    if (!Number.isFinite(id) || id <= 0) {
      this.errorMessage = 'Invalid leave policy id.';
      return;
    }

    this.policyId = id;
    this.loadPolicyDetail();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (this.assignModalOpen) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  get pageTitle(): string {
    return this.policy?.name || 'Leave Policy Details';
  }

  get hasAssignmentFilters(): boolean {
    return !!(
      this.assignmentFilters.searchTerm.trim() ||
      this.assignmentFilters.companyId !== null ||
      this.assignmentFilters.departmentId !== null
    );
  }

  get filteredAssignableUsers(): EmployeeUser[] {
    const search = this.assignmentFilters.searchTerm.trim().toLowerCase();

    return this.activeUsers.filter((user) => {
      const matchesSearch =
        !search ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(search) ||
        user.employeeNumber.toLowerCase().includes(search) ||
        (user.email ?? '').toLowerCase().includes(search);

      const matchesCompany =
        this.assignmentFilters.companyId === null ||
        user.company?.id === this.assignmentFilters.companyId;

      const matchesDepartment =
        this.assignmentFilters.departmentId === null ||
        user.department?.id === this.assignmentFilters.departmentId;

      return matchesSearch && matchesCompany && matchesDepartment;
    });
  }

  get assignmentSelectedCount(): number {
    return this.selectedEmployeeIds.size;
  }

  get leaveTypeClass(): string {
    return this.policy?.leaveType === 'Sick' ? 'sick' : 'vacation';
  }

  loadPolicyDetail(): void {
    if (!this.policyId) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    forkJoin({
      policy: this.accrualsService.getLeavePolicyById(this.policyId),
      users: this.accrualsService.getAllActiveUsers(),
      filters: this.accrualsService.getUserFilterValues(),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ policy, users, filters }) => {
          if (!policy) {
            this.errorMessage = 'Failed to load leave policy details.';
            return;
          }

          this.policy = policy;
          this.activeUsers = (users ?? []).filter(
            (user) => user.employeeStatus === 'Active' && !user.isDeleted,
          );
          this.userFilterValues = filters;
          this.cd.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Failed to load leave policy details.';
          this.cd.detectChanges();
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/settings/accruals/policies']);
  }

  openAssignPolicyModal(): void {
    this.assignmentStartDate = '';
    this.selectedEmployeeIds.clear();

    this.assignmentFilters = {
      searchTerm: '',
      companyId: null,
      departmentId: null,
    };

    this.assignmentSearchInputValue = '';

    this.assignModalOpen = true;
    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  closeAssignPolicyModal(): void {
    this.assignModalOpen = false;
    this.assignmentStartDate = '';
    this.selectedEmployeeIds.clear();
    this.clearAssignmentFilters();

    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  onAssignmentSearchChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.assignmentSearchInputValue = normalized;
    this.assignmentFilters.searchTerm = normalized;
    this.cd.detectChanges();
  }

  onAssignmentCompanyChange(value: number | string | null): void {
    this.assignmentFilters.companyId = this.toNullableNumber(value);
    this.cd.detectChanges();
  }

  onAssignmentDepartmentChange(value: number | string | null): void {
    this.assignmentFilters.departmentId = this.toNullableNumber(value);
    this.cd.detectChanges();
  }

  clearAssignmentFilters(): void {
    this.assignmentFilters = {
      searchTerm: '',
      companyId: null,
      departmentId: null,
    };

    this.assignmentSearchInputValue = '';
    this.cd.detectChanges();
  }

  toggleEmployeeSelection(employeeId: number): void {
    if (this.selectedEmployeeIds.has(employeeId)) {
      this.selectedEmployeeIds.delete(employeeId);
    } else {
      this.selectedEmployeeIds.add(employeeId);
    }

    this.cd.detectChanges();
  }

  isEmployeeSelected(employeeId: number): boolean {
    return this.selectedEmployeeIds.has(employeeId);
  }

  assignSelectedEmployees(): void {
    if (!this.policyId) {
      return;
    }

    if (!this.assignmentStartDate || this.selectedEmployeeIds.size === 0) {
      this.showToast('Select employees and a start date before saving', 'error');
      return;
    }

    this.isAssigningPolicy = true;
    this.cd.detectChanges();

    this.accrualsService
      .assignEmployeesToPolicy(this.policyId, {
        employeeIds: [...this.selectedEmployeeIds],
        startDate: this.assignmentStartDate,
      })
      .pipe(
        finalize(() => {
          this.isAssigningPolicy = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((success) => {
        if (!success) {
          this.showToast('Failed to assign policy to employees', 'error');
          return;
        }

        this.closeAssignPolicyModal();
        this.showToast('Policy assigned successfully', 'success');
        this.loadPolicyDetail();
      });
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    this.cd.detectChanges();

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }
}